export const SITE_EYEBROW = 'The AI-native PMS for dental groups';

export const NAV_ITEMS = [
  { label: 'Platform', to: '/alt4/platform' },
  { label: 'Autopilot', to: '/alt4/autopilot' },
  { label: 'Workflows', to: '/alt4/workflows' },
  { label: 'Multi-location', to: '/alt4/practices' },
  { label: 'Migration', to: '/alt4/migration' },
];

export const FOOTER_GROUPS = [
  {
    label: 'Explore',
    links: NAV_ITEMS,
  },
  {
    label: 'Legal',
    links: [
      { label: 'Privacy', to: '/privacy-policy-2026' },
      { label: 'Terms', to: '/terms-of-service' },
    ],
  },
];

export const OPERATING_LAYERS = [
  {
    title: 'Practice core',
    description: 'Schedules, patient records, providers, locations, permissions, and activity share one operational model.',
    detail: 'The system of record and the system of action stay in sync.',
  },
  {
    title: 'Patient access',
    description: 'Requests, scheduling, intake, and readiness move forward with the full patient and practice context.',
    detail: 'Every handoff begins from the same timeline.',
  },
  {
    title: 'Clinical workspace',
    description: 'Visit context, documentation, treatment details, and the next operational step remain connected.',
    detail: 'Clinical work can move cleanly into the office workflow.',
  },
  {
    title: 'Revenue operations',
    description: 'Insurance, claims, billing, payments, and follow-through operate from the completed visit context.',
    detail: 'Teams see what is moving, blocked, or ready for review.',
  },
  {
    title: 'Communications',
    description: 'Calls, texts, forms, and follow-up are coordinated through the same patient record and office rules.',
    detail: 'Conversations become actions without losing context.',
  },
  {
    title: 'Intelligence',
    description: 'Location queues, exceptions, and operating signals roll into a shared view for group leaders.',
    detail: 'Leaders can see execution without chasing updates.',
  },
];

export const WORKFLOW_GROUPS = [
  {
    id: 'patient-access',
    label: 'Patient access',
    summary: 'Turn a patient request into a prepared appointment while keeping the office rules in view.',
    trigger: 'A patient calls, texts, or submits a request.',
    context: 'Availability, location rules, patient history, and appointment requirements.',
    actions: ['Understand the request', 'Find the right opening', 'Confirm the visit', 'Start intake'],
    exception: 'Route schedule conflicts or policy questions to the right office queue.',
    completed: 'The appointment is confirmed and preparation is underway.',
  },
  {
    id: 'visit-readiness',
    label: 'Visit readiness',
    summary: 'Bring forms, coverage, reminders, and open questions together before the patient arrives.',
    trigger: 'An upcoming appointment enters its preparation window.',
    context: 'Visit type, forms, coverage details, communication history, and local policy.',
    actions: ['Check what is missing', 'Reach out in the right channel', 'Collect updates', 'Prepare the team'],
    exception: 'Surface incomplete or conflicting information for review.',
    completed: 'The visit is ready, with open items clearly assigned.',
  },
  {
    id: 'clinical-to-claim',
    label: 'Clinical to claim',
    summary: 'Carry completed visit context into documentation and claim preparation without a disconnected handoff.',
    trigger: 'The care team completes the visit.',
    context: 'Clinical record, procedures, coverage, documentation requirements, and practice rules.',
    actions: ['Confirm completion', 'Assemble required context', 'Check the work', 'Prepare the next action'],
    exception: 'Pause ambiguous details for an authorized reviewer.',
    completed: 'The claim workflow is prepared with its supporting context.',
  },
  {
    id: 'revenue-follow-through',
    label: 'Revenue follow-through',
    summary: 'Keep claims, payments, balances, and responses moving through one visible operations queue.',
    trigger: 'A claim, payment, response, or balance changes state.',
    context: 'Account history, claim status, office policy, communication history, and assigned ownership.',
    actions: ['Interpret the change', 'Update the record', 'Take the allowed next step', 'Watch for a response'],
    exception: 'Escalate policy, documentation, or account questions with full context attached.',
    completed: 'The record reflects the result and the next obligation is clear.',
  },
  {
    id: 'continuing-care',
    label: 'Continuing care',
    summary: 'Continue the patient relationship with timely follow-up grounded in care and communication history.',
    trigger: 'A continuing-care milestone or follow-up need becomes due.',
    context: 'Care plan, visit history, patient preferences, availability, and outreach rules.',
    actions: ['Identify the next need', 'Choose the appropriate outreach', 'Coordinate scheduling', 'Update the timeline'],
    exception: 'Send sensitive or unclear cases to the designated team.',
    completed: 'The patient has a clear next step and the office has a current record.',
  },
];

export const MIGRATION_STEPS = [
  {
    number: '01',
    title: 'Discover',
    description: 'Map locations, teams, data sources, operating policies, and the workflows that matter at launch.',
  },
  {
    number: '02',
    title: 'Map',
    description: 'Define the target operating model, including what stays consistent and what remains local.',
  },
  {
    number: '03',
    title: 'Configure',
    description: 'Prepare data, permissions, communications, and workflow rules in a controlled environment.',
  },
  {
    number: '04',
    title: 'Validate',
    description: 'Run readiness checks with the people who know each location and resolve exceptions before release.',
  },
  {
    number: '05',
    title: 'Launch & calibrate',
    description: 'Roll out by the sequence that fits the group, then tune workflows as the operating picture develops.',
  },
];
